import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, TrendingUp, Award } from "lucide-react";
import { useCaseStudies } from "@/data/hooks";
import Seo from "@/components/seo/Seo";

export default function CaseStudiesPage() {
  const { caseStudies, loading } = useCaseStudies();
  return (
    <div data-testid="case-studies-page" className="page-transition">
      <Seo
        title="Antera Case Studies | Real Customer Success Stories"
        description="Real distributors, suppliers, decorators, and print shops share how Antera Software helps them automate workflows, eliminate manual data entry, and grow."
        keywords="antera case studies, promotional products software case study, promo distributor success story, sign shop erp case study"
        path="/case-studies"
      />
      {/* Hero banner */}
      <section className="pt-28 lg:pt-36 bg-slate-900 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-[15%] w-72 h-72 bg-sky-500/10 rounded-full blur-[100px]" />
          <div className="absolute bottom-0 right-[10%] w-96 h-96 bg-sky-400/8 rounded-full blur-[120px]" />
          <svg className="absolute -bottom-[1px] left-0 w-full block" viewBox="0 0 1440 60" fill="none" preserveAspectRatio="none" aria-hidden="true">
            <path d="M0 61V30C240 0 480 0 720 30C960 60 1200 60 1440 30V61H0Z" fill="white" />
          </svg>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative pb-16 lg:pb-20">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <span className="text-xs font-semibold text-sky-400 tracking-widest uppercase">Antera Customers</span>
            <h1 className="mt-2 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white max-w-3xl">
              Case <span className="text-sky-400">Studies</span>
            </h1>
            <p className="mt-3 text-base text-slate-400 max-w-2xl">
              Real distributors, suppliers, decorators, and print shops sharing how they run faster and grow with Antera.
            </p>
            <p
              data-testid="case-studies-industry-line"
              className="mt-5 text-sm sm:text-base font-medium text-slate-300"
            >
              <span className="text-sky-400 font-extrabold">20 Years</span>{" "}
              servicing the Promo, Print and Signage Industries
            </p>
          </motion.div>
        </div>
      </section>

      {/* Listing */}
      <section className="py-14 lg:py-20 bg-slate-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 justify-items-stretch">
            {loading && caseStudies.length === 0 && (
              <p className="text-slate-400 col-span-full text-center py-8">Loading case studies…</p>
            )}
            {caseStudies.map((cs, i) => (
              <motion.article
                key={cs.slug}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08, duration: 0.4 }}
                data-testid={`case-study-card-${cs.slug}`}
                className="group bg-white border border-slate-200 rounded-2xl overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex flex-col"
              >
                <div className="aspect-[16/10] bg-slate-100 relative overflow-hidden">
                  {cs.image ? (
                    <img
                      src={cs.image}
                      alt={cs.company}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-slate-100 via-sky-50 to-slate-100 flex items-center justify-center">
                      <Award className="h-14 w-14 text-sky-300" />
                    </div>
                  )}
                  {!cs.published && (
                    <span className="absolute top-3 right-3 text-[10px] font-bold text-slate-600 bg-white/90 border border-slate-200 rounded-full px-2.5 py-1 uppercase tracking-wider">
                      Coming Soon
                    </span>
                  )}
                  <div className="absolute bottom-3 left-3 bg-white/95 backdrop-blur rounded-lg px-3 py-2 border border-slate-200 shadow-sm flex items-center gap-2">
                    <TrendingUp className="h-4 w-4 text-sky-600" />
                    <div>
                      <p className="text-base font-extrabold text-slate-900 leading-none">{cs.metric}</p>
                      <p className="text-[10px] text-slate-500 mt-0.5">{cs.metricLabel}</p>
                    </div>
                  </div>
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <span className="text-[10px] font-semibold text-sky-600 uppercase tracking-widest">{cs.industry}</span>
                  <h2 className="mt-2 text-lg font-bold text-slate-900 leading-snug group-hover:text-sky-600 transition-colors">
                    {cs.headline}
                  </h2>
                  <p className="mt-2 text-sm text-slate-500 leading-relaxed flex-1">{cs.excerpt}</p>
                  <div className="mt-5 pt-4 border-t border-slate-100 flex items-center justify-between">
                    <span className="text-xs font-medium text-slate-500">{cs.company}</span>
                    {cs.published ? (
                      <Link
                        to={`/case-studies/${cs.slug}`}
                        data-testid={`case-study-read-${cs.slug}`}
                        className="text-sm font-semibold text-sky-600 inline-flex items-center gap-1 group-hover:gap-2 transition-all"
                      >
                        Read story <ArrowRight className="h-3.5 w-3.5" />
                      </Link>
                    ) : (
                      <span className="text-xs text-slate-400">In production</span>
                    )}
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 lg:py-20 bg-sky-600 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-[20%] w-96 h-96 bg-sky-400/20 rounded-full blur-[120px]" />
          <div className="absolute bottom-0 right-[20%] w-72 h-72 bg-white/5 rounded-full blur-[100px]" />
        </div>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <h2 className="text-3xl sm:text-4xl font-bold text-white">Want your story featured?</h2>
          <p className="mt-3 text-sky-100">We love showcasing customer wins. Get in touch and we'll bring your team along for the ride.</p>
          <div className="mt-7">
            <Link to="/contact">
              <Button data-testid="case-studies-cta" className="bg-white text-sky-700 hover:bg-sky-50 rounded-full px-8 h-12 text-sm font-semibold">
                Get in touch <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
